import React, { useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "../../app/stores/store";
import orderPlacedGif from "../../assets/images/users/orderplaced.gif";
import {
  Box,
  Typography,
  Paper,
  CardMedia,
  Divider,
  Button,
  Radio,
  Stack,
  IconButton,
  Dialog,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { createOrder} from "../../app/services/OrderService";
import { motion } from "framer-motion";

const CheckoutPage: React.FC = observer(() => {
  const navigate = useNavigate();
  const [showSuccess, setShowSuccess] = useState(false);
  const [orderId, setOrderId] = useState<string>("");
  const { cartStore, outletStore } = useStore();
  const { selectedOutlet } = outletStore;

  const address = selectedOutlet
    ? `${selectedOutlet.shipping_addressline1 ?? ""} ${
        selectedOutlet.shipping_addressline2 ?? ""
      } ${selectedOutlet.shippingcity ?? ""}, ${
        selectedOutlet.shippingState?.name ?? ""
      } - ${selectedOutlet.shippingpincode ?? ""}`
    : "No outlet address available";

  useEffect(() => {
    if (selectedOutlet) {
      cartStore.loadCartItems(selectedOutlet.id);
    }
  }, [cartStore, selectedOutlet]);


  // ✅ Dynamic GST calculation based on each product.tax
  let itemTotal = 0;
  let totalGst = 0;

  cartStore.cart.forEach((item) => {
    if (!item.product) return;

    const price =
      item.custom_price && parseFloat(item.custom_price) > 0
        ? parseFloat(item.custom_price)
        : parseFloat(item.product.base_price ?? "0");

    const quantity = Number(item.quantity) || 0;
    const total = price * quantity;

    // GST rate comes from product.tax (e.g., "5", "12", "18")
    const gstRate = parseFloat(item.product.tax ?? "0");
    const gstAmount = (total * gstRate) / 100;

    itemTotal += total;
    totalGst += gstAmount;
  });

  const totalAmount = itemTotal + totalGst;

  const handlePlaceOrder = async () => {
    try {
      const outlet_id = selectedOutlet?.id;

      if (!outlet_id) {
        throw new Error("Outlet ID is missing");
      }

      const result = await createOrder({ outlet_id });

      if (
        typeof result === "object" &&
        result !== null &&
        "status" in result &&
        (result as { status?: string }).status === "success"
      ) {
        const { data } = result as {
          status: string;
          data: any;
          message: string;
        };
        setOrderId(data.unique_id);
        setShowSuccess(true);
      } else {
       
        toast(
          typeof result === "object" && result !== null && "message" in result
            ? (result as { message?: string }).message
            : "Something went wrong"
        );
      }
    } catch (error: any) {
      let errorMessage = "Something went wrong";

      if (error.response) {
        const { data, status } = error.response;
        errorMessage =
          data?.message ||
          data?.error ||
          `Request failed with status code ${status}`;
      } else if (error.request) {
        errorMessage = "No response received from server";
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast(errorMessage || "Something went wrong", { type: "error" });
    }
  };

  const handleClose = () => {
    setShowSuccess(false);
    navigate("/order");
  };

  return (
    <Box
      display="flex"
      gap={3}
      padding={3}
      sx={{ background: "#f4f6f8", minHeight: "100vh" }}
    >
      {/* LEFT SIDE: Product List */}
      <motion.div
        initial={{ opacity: 0, x: -80 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6 }}
        style={{ flex: 2 }}
      >
        <Paper elevation={1} sx={{ p: 2 }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" fontWeight={700}>
              Checkout
            </Typography>
          </Box>

          <Box display="flex" px={1.5} py={1} fontWeight={600} color="black">
            <Box flex={4}>Products ({cartStore.cart.length})</Box>
            <Box flex={1} textAlign="center">
              Quantity
            </Box>
            <Box flex={1} textAlign="right">
              Amount
            </Box>
          </Box>
          <Divider />

          {cartStore.cart.length === 0 ? (
            <Box textAlign="center" p={2}>
              <Typography variant="body1" color="text.secondary">
                Your cart is empty
              </Typography>
            </Box>
          ) : (
            cartStore.cart.map((item) => {
              if (!item.product) {
                return (
                  <Typography
                    key={item.id}
                    color="error"
                    variant="body2"
                    sx={{ p: 1 }}
                  >
                    Invalid product data
                  </Typography>
                );
              }

              const price =
                item.custom_price && parseFloat(item.custom_price) > 0
                  ? parseFloat(item.custom_price)
                  : parseFloat(item.product.base_price ?? "0");
              const quantity = Number(item.quantity) || 0;
              const total = price * quantity;
              const gstRate = parseFloat(item.product.tax ?? "0");
              const gstAmount = (total * gstRate) / 100;

              return (
                <Box
                  key={item.id}
                  display="flex"
                  alignItems="center"
                  px={1.5}
                  py={2}
                  borderBottom="1px solid #eee"
                >
                  <Box flex={4} display="flex" alignItems="center">
                    <CardMedia
                      component="img"
                      image={
                        item.product.images?.[0]?.path || "/placeholder.jpg"
                      }
                      alt={item.product.name ?? "Product"}
                      sx={{
                        width: 50,
                        height: 50,
                        objectFit: "cover",
                        borderRadius: 1,
                        mr: 1.5,
                      }}
                    />
                    <Box>
                      <Typography fontWeight={600}>
                        {item.product.name ?? "Unnamed Product"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Tax: {gstRate}% | ₹{gstAmount.toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box flex={1} textAlign="center">
                    {quantity.toString().padStart(3, "0")}
                  </Box>
                  <Box flex={1} textAlign="right">
                    ₹ {total.toFixed(2)}
                  </Box>
                </Box>
              );
            })
          )}
        </Paper>
      </motion.div>

      {/* RIGHT SIDE: Bill Summary + Address */}
      <Box flex={1} display="flex" flexDirection="column" gap={2}>
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography variant="h5" fontWeight={600} mb={2}>
            💰 Bill Summary
          </Typography>

          <Stack direction="row" justifyContent="space-between" mb={1}>
            <Typography>Item Total</Typography>
            <Typography fontWeight={600}>₹ {itemTotal.toFixed(2)}</Typography>
          </Stack>

          <Stack direction="row" justifyContent="space-between" mb={1}>
            <Typography>GST Total</Typography>
            <Typography fontWeight={600}>₹ {totalGst.toFixed(2)}</Typography>
          </Stack>

          <Divider sx={{ my: 1 }} />

          <Stack direction="row" justifyContent="space-between">
            <Typography fontWeight={700}>Total Payable</Typography>
            <Typography fontWeight={700} color="primary">
              ₹ {totalAmount.toFixed(2)}
            </Typography>
          </Stack>
        </Paper>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={2}>
              🛒 Confirm Address
            </Typography>
            <Box display="flex" alignItems="flex-start">
              <Radio checked />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {address}
                </Typography>
              </Box>
            </Box>
            <Button
              fullWidth
              variant="contained"
              color="success"
              sx={{ mt: 2 }}
              onClick={handlePlaceOrder}
            >
              Place Order
            </Button>
          </Paper>
        </motion.div>
      </Box>

      {/* ✅ Success Dialog */}
      <Dialog open={showSuccess} onClose={handleClose}>
        <Box p={4} textAlign="center" borderRadius={2}>
          <img
            src={orderPlacedGif}
            alt="Order Placed"
            style={{
              width: "150px",
              marginBottom: "16px",
            }}
          />
          <Typography variant="h6" mt={2}>
            Order Placed Successfully
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            Order Id #{orderId}
          </Typography>
        </Box>
      </Dialog>
    </Box>
  );
});

export default CheckoutPage;
